import { useMutation, useQueryClient } from 'react-query';
import api from 'services';
import { showNotification } from 'helpers';

const handleError = (message) => {
    showNotification(message, 'error');
};

const handleSuccess = (message) => {
    showNotification(message, 'success');
};

/**
 * hook for port-related mutations
 * @param {string} portId - The ID of the port
 */
export const usePortMutations = (portId) => {
    const queryClient = useQueryClient();

    /** @param {void} - invalidates port queries */
    const invalidatePort = () => queryClient.invalidateQueries(['port', portId]);

    /**
     * mutation for approving port modifications
     * @type {import('react-query').UseMutationResult<*, Error, PortModification>}
     */
    const approve = useMutation(
        ({ _id, historyId, updateAt }) => api.approvePortModifications({ _id, historyId, updateAt }),
        { 
            onSuccess: () => {
                invalidatePort();
                handleSuccess('Port modifications approved');
            }, 
            onError: handleError,
        },
    );

    /**
     * mutation for rejecting port modifications
     * @type {import('react-query').UseMutationResult<*, Error, PortModification>}
     */
    const reject = useMutation(
        ({ _id, historyId, updateAt }) => api.rejectPortModifications({ _id, historyId, updateAt }),
        { 
            onSuccess: () => {
                invalidatePort();
                handleSuccess('Port modifications rejected');
            }, 
            onError: handleError,
        },
    );
    
    /**
     * mutation for updating a single field
     * @type {import('react-query').UseMutationResult<*, Error, PortModification>}
     */
    const updateField = useMutation(
        ({
            _id, historyId, updateAt, toUpdate, 
        }) => api.updatePortModificationField({
            _id, historyId, updateAt, toUpdate, 
        }),
        { 
            onSuccess: () => {
                invalidatePort();
                handleSuccess('Port modification field updated');
            }, 
            onError: handleError,
        },
    );

    /**
     * mutation for approving a single field
     * @type {import('react-query').UseMutationResult<*, Error, PortModification>}
     */
    const approveField = useMutation(
        ({
            _id, historyId, fields, updateAt, 
        }) => api.approvePortModificationField({
            _id, historyId, fields, updateAt, 
        }),
        { 
            onSuccess: () => {
                invalidatePort();
                handleSuccess('Port modification field approved');
            }, 
            onError: handleError,
        },
    );

    /**
     * mutation for rejecting a single field
     * @type {import('react-query').UseMutationResult<*, Error, PortModification>}
     */
    const rejectField = useMutation(
        ({
            _id, historyId, fields, updateAt, 
        }) => api.rejectPortModificationField({
            _id, historyId, fields, updateAt, 
        }),
        { 
            onSuccess: () => {
                invalidatePort();
                handleSuccess('Port modification field rejected');
            }, 
            onError: handleError,
        },
    );


    return {
        approve, reject, updateField, approveField, rejectField, 
    };
};

/**
 * Hook for service provider-related mutations
 * @param {string} portId - The ID of the port
 */
export const useServiceProvidersMutations = (portId) => {
    const queryClient = useQueryClient();
    const invalidatePort = () => queryClient.invalidateQueries(['port', portId]);
    
    /**
     * mutation for approving a service provider
     * @type {import('react-query').UseMutationResult<*, Error, PortModification>}
     */
    const approve = useMutation(
        ({ _id, historyId, updateAt }) => api.approveServiceProvider({ _id, historyId, updateAt }),
        { 
            onSuccess: () => {
                invalidatePort();
                handleSuccess('Service provider approved');
            }, 
            onError: handleError,
        },
    );

    /**
     * mutation for rejecting a service provider
     * @type {import('react-query').UseMutationResult<*, Error, PortModification>}
     */
    const reject = useMutation(
        ({ _id, historyId, updateAt }) => api.rejectServiceProvider({ _id, historyId, updateAt }),
        { 
            onSuccess: () => {
                invalidatePort();
                handleSuccess('Service provider rejected');
            }, 
            onError: handleError,
        },
    );
    
    /**
     * mutation for updating a service provider
     * @type {import('react-query').UseMutationResult<*, Error, PortModification>}
     */
    const update = useMutation(
        ({
            _id, historyId, updateAt, serviceProviders,
        }) => api.updateServiceProviders({
            _id, historyId, updateAt, serviceProviders,
        }),
        { 
            onSuccess: () => {
                invalidatePort();
                handleSuccess('Service provider updated');
            }, 
            onError: handleError,
        },
    );

    /**
     * mutation for approving a new service provider
     * @type {import('react-query').UseMutationResult<*, Error, PortModification>}
     */
    const approveNew = useMutation(
        ({
            _id, historyId, updateAt, providerId, 
        }) => api.approveNewServiceProvider({
            _id, historyId, updateAt, providerId, 
        }),
        {
            onSuccess: () => {
                invalidatePort();
                handleSuccess('New service provider approved');
            },
            onError: handleError,
        },
    );

    /**
     * mutation for rejecting a new service provider
     * @type {import('react-query').UseMutationResult<*, Error, PortModification>}
     */
    const rejectNew = useMutation(
        ({
            _id, historyId, updateAt, providerId, 
        }) => api.rejectNewServiceProvider({
            _id, historyId, updateAt, providerId, 
        }),
        {
            onSuccess: () => {
                invalidatePort();
                handleSuccess('New service provider rejected');
            },
            onError: handleError,
        },
    );

    /**
     * mutation for approving a remove service provider
     * @type {import('react-query').UseMutationResult<*, Error, PortModification>}
     */
    const approveRemove = useMutation(
        ({
            _id, historyId, updateAt, providerId, 
        }) => api.approveRemoveServiceProvider({
            _id, historyId, updateAt, providerId, 
        }),
        {
            onSuccess: () => {
                invalidatePort();
                handleSuccess('Remove service provider approved');
            },
            onError: handleError,
        },
    );

    /**
     * mutation for rejecting a remove service provider
     * @type {import('react-query').UseMutationResult<*, Error, PortModification>}
     */
    const rejectRemove = useMutation(
        ({
            _id, historyId, updateAt, providerId, 
        }) => api.rejectRemoveServiceProvider({
            _id, historyId, updateAt, providerId, 
        }),
        {
            onSuccess: () => {
                invalidatePort();
                handleSuccess('Remove service provider rejected');
            },
            onError: handleError,
        },
    );

    return {
        approveServiceProvider: approve,
        rejectServiceProvider: reject,
        updateServiceProvider: update,
        approveNewServiceProvider: approveNew,
        rejectNewServiceProvider: rejectNew,
        approveRemoveServiceProvider: approveRemove,
        rejectRemoveServiceProvider: rejectRemove,
    };
};

/**
 * @typedef {Object} PortModification
 * @property {string} _id - port id
 * @property {string} historyId - history record id
 * @property {number} [updateAt] - timestamp of the update
 * @property {Record<string, any>} [toUpdate] - field update data
 * @property {string} [toUpdate.field] - field name to update
 * @property {string[]} [toApprove] - field name to approve
 * @property {string[]} [toReject] - field name to reject
 * @property {*} [toUpdate.value] - new value for the field
 * @property {string[]} [fields] - fields to approve or reject
 * @property {any[]} [serviceProviders] - service providers to update
 * @property {string} [providerId] - provider id to approve or reject
 */



// WEBPACK FOOTER //
// ./app/components/Regions/PortManagement/hooks/ports/mutations.js